import type { GameType } from '@hit-game/shared/enums';
import type { IAccountSessionBets, IGroup, IGroupSessionEntry } from '@hit-game/shared/types';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { groupService } from '../../services/group.service';
import { formatDateTime, formatMoney } from '../../utils/format';
import { Modal } from '../common/Modal';
import { GAME_BADGES, GAME_LABELS, MEMBER_COLORS } from '../group/group.constants';

/** Số phiên xin về. Server chặn trần 50. */
const PAGE_SIZE = 20;

/** Nhịp làm mới khi modal đang mở — giống tool-no-hu (3 giây). */
const REFRESH_MS = 3_000;

/**
 * Ván mới đặt chưa có kết quả thì hiện "Chờ kết quả…". Quá hạn này mà vẫn trống nghĩa là lịch sử cược
 * của nhà cái đã quá hạn/không khớp — hiện "—" chứ không chờ mãi.
 */
const RESULT_WAIT_MS = 5 * 60_000;

interface GroupSessionModalProps {
  group: IGroup | null;
  onClose: () => void;
}

/** Màu theo chỗ của thành viên trong nhóm — trùng màu tên ở thẻ nhóm. */
function memberColor(group: IGroup, accountId: string): string | undefined {
  const index = group.members.findIndex((member) => String(member.accountId) === accountId);
  return index < 0 ? undefined : MEMBER_COLORS[index % MEMBER_COLORS.length];
}

/**
 * Nhãn viết tắt của từng cửa — CÙNG bộ với tin Telegram (`formatAllocations` bên
 * server): Chẵn→C, Lẻ→L, Tài→T, Xỉu→X, 3 đỏ 1 trắng→3D, 3 trắng 1 đỏ→3T.
 */
const BET_KEY_LABEL: Record<string, string> = {
  TAI: 'T',
  XIU: 'X',
  CHAN: 'C',
  LE: 'L',
  '4T': '4T',
  '4D': '4D',
  '3T1D': '3T',
  '3D1T': '3D',
  '2D2T': '2D2T',
  BAU: 'Bầu',
  CUA: 'Cua',
  CA: 'Cá',
  TOM: 'Tôm',
  GA: 'Gà',
  NAI: 'Nai',
};

/** Tiền gọn theo nghìn (104.000 → "104k"); không chia hết thì in đủ để khỏi nuốt phần lẻ. */
function formatShortMoney(amount: number): string {
  return amount % 1000 === 0 ? `${formatMoney(amount / 1000)}k` : formatMoney(amount);
}

/** "L 52k 3D 26k 3T 26k" — cộng theo cửa, giữ thứ tự cửa xuất hiện đầu tiên. */
export function formatBetBreakdown(bets: IAccountSessionBets['bets']): string {
  const totals = new Map<string, number>();
  for (const bet of bets) {
    const label = BET_KEY_LABEL[String(bet.eid)] ?? String(bet.eid);
    totals.set(label, (totals.get(label) ?? 0) + bet.amount);
  }
  return [...totals].map(([label, amount]) => `${label} ${formatShortMoney(amount)}`).join(' ');
}

/** Lãi/lỗ có dấu — số dương luôn kèm '+'. */
function formatSigned(delta: number): string {
  return delta > 0 ? `+${formatMoney(delta)}` : formatMoney(delta);
}

/**
 * Một ô: cách đánh (dòng 1) + lãi/lỗ và số dư sau ván (dòng 2) — đọc y như dòng
 * của tin Telegram. Thiếu một đầu mốc số dư thì hiện "Chờ lãi lỗ…" chứ không bịa.
 */
function AccountCell({ row }: { row: IAccountSessionBets | undefined }) {
  if (!row || row.bets.length === 0) return <span className="text-ink-dim">—</span>;

  const settled =
    row.delta !== undefined && row.balanceAfter !== undefined
      ? { delta: row.delta, balanceAfter: row.balanceAfter }
      : null;

  return (
    <div className="flex flex-col gap-0.5 whitespace-nowrap font-mono text-[11px]">
      <span className="text-ink font-bold">{formatBetBreakdown(row.bets)}</span>
      {settled ? (
        <span className="font-semibold">
          <span
            className={
              settled.delta > 0
                ? 'text-success'
                : settled.delta < 0
                  ? 'text-danger'
                  : 'text-ink-dim'
            }
          >
            {settled.delta > 0 ? 'WIN' : settled.delta < 0 ? 'LOSE' : 'DRAW'}{' '}
            {formatSigned(settled.delta)}
          </span>
          <span className="text-ink-faint"> | </span>
          <span className="text-ink-muted">{formatMoney(settled.balanceAfter)}</span>
        </span>
      ) : (
        <span className="text-warning/70 font-semibold">Chờ lãi lỗ...</span>
      )}
    </div>
  );
}

function ResultCell({ entry }: { entry: IGroupSessionEntry }) {
  if (!entry.result && entry.resultRaw === null) {
    return Date.now() - entry.timestamp < RESULT_WAIT_MS ? (
      <span className="text-warning/70 text-[11px] font-semibold">Chờ kết quả...</span>
    ) : (
      <span className="text-ink-dim">—</span>
    );
  }

  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span className="text-ink font-mono text-[12px]">
        {entry.result?.dice ? `[${entry.result.dice.join(',')}]` : (entry.resultRaw ?? '')}
      </span>
      {entry.result?.total !== undefined && (
        <span className="text-ink-dim font-mono text-[11px]">= {entry.result.total}</span>
      )}
      {entry.betTypeResult !== null && (
        <span className="bg-accent-soft text-accent-bright rounded px-1.5 py-0.5 text-[10px] font-bold">
          {entry.betTypeResult}
        </span>
      )}
    </span>
  );
}

/**
 * Các phiên nhóm vừa đánh — mỗi dòng là MỘT ván, mỗi thành viên một cột (cửa đã
 * đánh + lãi/lỗ + số dư sau ván), cuối là kết quả ván.
 *
 * Khung ván đọc từ `RoundLog` server ghi lúc chốt số — cùng con số đã gửi lên
 * Telegram — nên còn nguyên sau restart và không bị giới hạn 1 giờ như Lịch Sử Cược
 * của nhà cái. Chỉ ô "Kết quả" cần Lịch Sử Cược đã đồng bộ (khớp theo số phiên).
 */
export function GroupSessionModal({ group, onClose }: GroupSessionModalProps) {
  const [entries, setEntries] = useState<IGroupSessionEntry[]>([]);
  const [unreadable, setUnreadable] = useState(0);
  const [loading, setLoading] = useState(false);

  const groupId = group ? String(group._id) : null;

  // Một cột cho MỖI thành viên (đủ 4 người). Tên lấy từ chính các ván đã lưu: nhóm
  // chỉ giữ id thành viên, còn username nằm trong từng ô của ván.
  const memberCols = useMemo(() => {
    const names = new Map<string, string>();
    for (const entry of entries) {
      for (const row of entry.accountBets) names.set(row.accountId, row.username);
    }
    const ids = group?.members.map((member) => String(member.accountId)) ?? [];
    return ids.map((accountId) => ({ accountId, username: names.get(accountId) ?? accountId }));
  }, [entries, group]);

  // Mở modal là tải ngay (có spinner), rồi tự làm mới mỗi REFRESH_MS: ván đang chạy hiện ngay khi
  // đặt xong, lãi/lỗ và kết quả tự điền vào — không phải tắt mở lại. Dừng hẳn khi đóng modal.
  useEffect(() => {
    if (groupId === null) {
      setEntries([]);
      setUnreadable(0);
      return;
    }

    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = async (first: boolean): Promise<void> => {
      if (first) setLoading(true);
      try {
        const data = await groupService.history(groupId, PAGE_SIZE);
        if (!alive) return;
        setEntries(data.entries);
        setUnreadable(data.unreadable);
      } catch {
        // Toast lỗi do interceptor bắn; lượt sau thử lại.
      } finally {
        if (alive) {
          if (first) setLoading(false);
          // Đệ quy sau khi lượt này XONG (không setInterval) — mạng chậm thì không chồng request.
          timer = setTimeout(() => {
            void load(false);
          }, REFRESH_MS);
        }
      }
    };

    void load(true);

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [groupId]);

  return (
    <Modal
      open={group !== null}
      onClose={onClose}
      maxWidth={1040}
      title={
        <span className="flex items-center gap-2">
          Phiên đã đánh
          <span className="text-ink-dim text-[12px] font-normal">{group?.name}</span>
        </span>
      }
    >
      {loading ? (
        <div className="text-ink-dim flex items-center justify-center gap-2 py-10 text-[13px]">
          <Loader2 size={16} className="animate-spin" />
          Đang tải...
        </div>
      ) : entries.length === 0 ? (
        <div className="text-ink-dim space-y-2 py-10 text-center text-[13px]">
          <p className="font-semibold">Chưa có phiên nào đọc được</p>
          <p className="text-ink-faint mx-auto max-w-md text-[11px] leading-relaxed">
            Bảng này ghi lại từng ván nhóm đã đánh và đã chốt số (cùng con số gửi lên Telegram).
            Chạy nhóm vài ván sẽ có dữ liệu; ô "Kết quả" lấy từ Lịch sử cược đã đồng bộ.
          </p>
        </div>
      ) : (
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full border-collapse text-left">
            <thead className="bg-canvas-raised sticky top-0">
              <tr>
                {['Phiên', ...memberCols.map((member) => member.username), 'Kết quả'].map(
                  (column) => (
                    <th
                      key={column}
                      className="text-ink-faint px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em]"
                    >
                      {column}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.sessionId} className="border-line hover:bg-panel-hover border-t">
                  <td className="whitespace-nowrap px-3 py-2 align-top">
                    <div className="flex items-center gap-1.5">
                      <span className="text-accent font-mono text-[12px] font-bold">
                        #{entry.sessionId}
                      </span>
                      {/* Nhóm đổi game được nên mỗi ván phải nói rõ nó thuộc game nào. */}
                      <span
                        className={`rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-wider ${
                          GAME_BADGES[entry.gameType as GameType] ?? 'bg-panel-hover text-ink-dim'
                        }`}
                        title={GAME_LABELS[entry.gameType as GameType] ?? entry.gameType}
                      >
                        {entry.gameType}
                      </span>
                    </div>
                    <div className="text-ink-muted font-mono text-[10px]">
                      {formatDateTime(Math.floor(entry.timestamp / 1000))}
                    </div>
                  </td>
                  {memberCols.map((member) => (
                    <td key={member.accountId} className="px-3 py-2 align-top">
                      <AccountCell
                        row={entry.accountBets.find((row) => row.accountId === member.accountId)}
                      />
                    </td>
                  ))}
                  <td className="px-3 py-2 align-top">
                    <ResultCell entry={entry} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dòng cược có thật nhưng KHÔNG đọc được số phiên. Câu mô tả của nhà cái
          mới chỉ verify trên Tài Xỉu Live, nên con số này là dấu hiệu duy nhất
          nhìn thấy được khi Xóc Đĩa (hoặc một bản cập nhật nào đó) đổi chữ. */}
      {unreadable > 0 && (
        <div className="border-warning/35 bg-warning/10 text-warning mt-3 flex items-start gap-2 rounded-md border px-3 py-2 text-[11px] font-medium">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>
            {unreadable} dòng cược không đọc được số phiên nên không ghép được vào bảng — xem nguyên
            văn ở "Lịch sử cược" của từng tài khoản.
          </span>
        </div>
      )}
    </Modal>
  );
}
